import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Trash2, RefreshCw, ExternalLink, Copy, Check, Calendar, X, Sparkles, Users } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import {
  useDomains,
  useAddDomain,
  useRefreshDomain,
  useDeleteDomain,
  type DomainRow,
} from '@/lib/domains';
import { useMe, useBrief, useUpdateBriefSlice } from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/settings')({
  staticData: { crumb: 'Ustawienia' },
  component: SettingsPage,
});

function SettingsPage() {
  const me = useMe();
  const tenant = me.data?.tenant;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Ustawienia
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Identyfikatory konta + zarządzanie własną domeną.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Identyfikatory</CardTitle>
          <CardDescription>
            Te wartości pojawiają się w niektórych adresach URL i webhookach.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row label="Slug kliniki" value={tenant?.slug ?? '—'} mono />
          <Row label="Tenant ID" value={tenant?.id ?? '—'} mono />
          <Row label="Token podglądu" value={tenant?.preview_token ?? '—'} mono />
          <Row
            label="Domena podglądu"
            value={tenant ? `${tenant.preview_token}.pozyskajpacjenta.pl` : '—'}
            mono
          />
          <Row label="Plan" value={(tenant?.tier ?? '—').toUpperCase()} />
        </CardContent>
      </Card>

      <BookingMethodCard />

      <BooksyCard />

      <DomainsCard />

      <InvoicingCard />

      <RodoCard />
    </div>
  );
}

/* ─────────────── Invoicing (#137) ───────────────
 *
 * No Stripe portal CTA — we manually invoice the first 10 paying clinics
 * (per docs/audit/SYNTHESIS.md). Owner sees what they pay, where the
 * invoice arrives, and how to reach us if anything is wrong. When/if
 * Stripe live mode flips on, this card adds a real "Manage subscription"
 * button instead of the contact line.
 */
function InvoicingCard() {
  const me = useMe();
  const { data: brief } = useBrief();
  const tier = me.data?.tenant?.tier ?? 'preview';
  const tierLabel =
    tier === 'pozyskaj' || tier === 'start'
      ? 'Start (99 zł / mies. lub 890 zł / rok)'
      : tier === 'pro' || tier === 'pro_bundle'
        ? 'Pro (299 zł / mies. lub 2 990 zł / rok)'
        : tier === 'multi'
          ? 'Multi (599 zł / mies. per lokalizacja)'
          : 'Podgląd (jeszcze nie aktywowany)';
  const billingEmail =
    (brief as { contact?: { email?: string }; company?: { email?: string } } | undefined)
      ?.contact?.email ??
    (brief as { company?: { email?: string } } | undefined)?.company?.email ??
    '—';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Faktury i płatność</CardTitle>
        <CardDescription>
          Wystawiamy fakturę VAT ręcznie na koniec każdego miesiąca. Bez automatycznego
          obciążania karty — pierwsze 10 klinik traktujemy jako kohortę założycielską.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Row label="Plan" value={tierLabel} />
        <Row label="Email do faktur" value={billingEmail} mono />
        <Row
          label="Częstotliwość"
          value="Miesięcznie (lub rocznie, jeśli zdecydujesz)"
        />
        <div className="rounded-md bg-surface p-3 text-xs text-text-muted ring-1 ring-black/5">
          Coś nie pasuje? Napisz do{' '}
          <a
            href="mailto:max@pozyskajpacjenta.pl?subject=Faktura"
            className="text-accent hover:underline"
          >
            max@pozyskajpacjenta.pl
          </a>{' '}
          — odpowiadamy w 24 h. Wystawiamy faktury proforma i końcowe; akceptujemy
          przelewy na konto firmowe H2M sp. z o.o.
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────── RODO + sub-processors (#137) ───────────────
 *
 * Per RODO art. 30 we must list sub-processors and offer a DPA template.
 * @h2m/compliance keeps the canonical list — link out to the rendered
 * privacy policy + a button to fetch the DPA template (already on the
 * api-worker side via compliance/dpa).
 */
function RodoCard() {
  const me = useMe();
  const tenant = me.data?.tenant;
  const previewBase = tenant?.slug ? `${tenant.slug}.pozyskajpacjenta.pl` : null;
  const privacyUrl = previewBase ? `https://${previewBase}/polityka-prywatnosci` : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>RODO i bezpieczeństwo danych</CardTitle>
        <CardDescription>
          Polityka prywatności jest generowana automatycznie pod adresem Twojej strony.
          Lista sub-procesorów (Cloudflare, SMSAPI, Stripe…) jest częścią polityki.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Row label="Wersja polityki" value="2026.01 (aktualna)" />
        <Row
          label="Sub-procesorzy"
          value="Cloudflare · OUTBOUND_EMAIL · SMSAPI (opcj.) · Stripe (opcj.)"
        />
        {privacyUrl ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="secondary" size="sm">
              <a href={privacyUrl} target="_blank" rel="noreferrer">
                Otwórz politykę
                <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <a href="mailto:max@pozyskajpacjenta.pl?subject=DPA%20-%20wzór%20umowy">
                Poproś o DPA
              </a>
            </Button>
          </div>
        ) : (
          <p className="text-xs text-text-muted">
            Polityka będzie dostępna po pierwszej publikacji strony.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────── Booking method picker ─────────────── */

type BookingProvider = 'in_house' | 'znanylekarz_weterynaria' | 'booksy' | 'none';

function BookingMethodCard() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const current = ((brief as BriefShape | undefined)?.booking?.provider ?? 'none') as BookingProvider;
  const [busy, setBusy] = React.useState(false);

  const pick = async (next: BookingProvider) => {
    if (next === current) return;
    setBusy(true);
    try {
      const prev = (brief as BriefShape | undefined)?.booking ?? {};
      await update.mutateAsync({
        slice: 'booking',
        data: { ...prev, provider: next },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="size-5" aria-hidden="true" /> Metoda rezerwacji online
        </CardTitle>
        <CardDescription>
          Wybierz, jak pacjenci umawiają wizyty na stronie. Zalecamy własny system —
          jest darmowy, działa od razu, nie pobiera prowizji i daje pełną kontrolę
          nad kalendarzem.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul role="list" className="grid gap-3 md:grid-cols-2">
          <ProviderOption
            id="in_house"
            current={current}
            onPick={pick}
            busy={busy}
            recommended
            icon={Sparkles}
            title="Nasz system (pozyskajpacjenta.pl)"
            badge="Zalecane"
            description={
              'Pełny white-label. Pacjent rezerwuje bez wychodzenia ze strony ' +
              'kliniki. Brak prowizji. Powiadomienia e-mail + SMS. T-24h przypomnienia.'
            }
            cta={{ to: '/calendar', label: 'Skonfiguruj harmonogram →' }}
          />
          <ProviderOption
            id="znanylekarz_weterynaria"
            current={current}
            onPick={pick}
            busy={busy}
            icon={Users}
            title="znanylekarz.pl"
            description={
              'Trzecia strona; każdy lekarz ma swój profil. Konfiguracja w Treści ' +
              'strony → Zespół, edytor lekarza, przycisk „Skonfiguruj rezerwację".'
            }
            cta={{ to: '/brief/team', label: 'Edytuj zespół →' }}
          />
          <ProviderOption
            id="booksy"
            current={current}
            onPick={pick}
            busy={busy}
            icon={Calendar}
            title="Booksy.com"
            description={
              'Trzecia strona; jeden profil per klinika. Konfiguracja poniżej ' +
              'w sekcji „Rezerwacja online — Booksy".'
            }
          />
          <ProviderOption
            id="none"
            current={current}
            onPick={pick}
            busy={busy}
            icon={X}
            title="Brak (tylko telefon / formularz)"
            description="Strona pokazuje numer telefonu + formularz kontaktowy. Bez wbudowanego kalendarza."
          />
        </ul>
      </CardContent>
    </Card>
  );
}

function ProviderOption({
  id,
  current,
  onPick,
  busy,
  recommended,
  icon: Icon,
  title,
  badge,
  description,
  cta,
}: {
  id: BookingProvider;
  current: BookingProvider;
  onPick: (id: BookingProvider) => void;
  busy: boolean;
  recommended?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  badge?: string;
  description: string;
  cta?: { to: string; label: string };
}) {
  const active = current === id;
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(id)}
        disabled={busy}
        className={cn(
          'group block w-full rounded-md p-4 text-left ring-1 transition',
          active
            ? 'bg-accent/10 ring-accent/40'
            : recommended
            ? 'bg-success/5 ring-success/20 hover:ring-success/40'
            : 'bg-surface ring-black/5 hover:ring-black/10'
        )}
      >
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'grid size-9 place-items-center rounded-md ring-1',
              active
                ? 'bg-accent/15 text-accent ring-accent/30'
                : 'bg-bg text-text-muted ring-border'
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <p className="font-display text-sm font-semibold text-text">{title}</p>
              {badge ? (
                <span className="rounded-full bg-success/10 px-2 py-0.5 text-[10px] text-success ring-1 ring-success/20">
                  {badge}
                </span>
              ) : null}
              {active ? (
                <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent ring-1 ring-accent/20">
                  Wybrane
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-text-muted">{description}</p>
          </div>
        </div>
      </button>
      {active && cta ? (
        <Link
          to={cta.to as never}
          className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
        >
          {cta.label}
        </Link>
      ) : null}
    </li>
  );
}

/* ─────────────── Booksy binding ─────────────── */

interface BooksyBinding {
  business_id: number;
  profile_url: string;
  widget_type?: 'full' | 'compact';
  hide_branding?: boolean;
}

interface BriefShape {
  booking?: {
    provider?: BookingProvider;
    booksy?: BooksyBinding;
  };
}

/**
 * Booksy profile URL parser. Formats observed in the wild:
 *   https://booksy.com/pl-pl/123456,nazwa-kliniki
 *   https://booksy.com/en-gb/123456_nazwa-kliniki
 *   https://booksy.com/widget/index.html?id=123456
 */
function parseBooksyUrl(input: string):
  | { business_id: number; normalized_url: string }
  | { error: string } {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    return { error: 'Nieprawidłowy URL.' };
  }
  if (!u.host.endsWith('booksy.com')) {
    return { error: 'URL musi pochodzić z booksy.com.' };
  }
  // /widget/index.html?id=NNN
  const idQuery = u.searchParams.get('id');
  if (idQuery && /^\d+$/.test(idQuery)) {
    return { business_id: Number(idQuery), normalized_url: u.toString() };
  }
  // /pl-pl/NNNNNN,slug
  const m = /\/(\d{4,12})[,_-]/.exec(u.pathname);
  if (m) {
    return { business_id: Number(m[1]), normalized_url: u.toString() };
  }
  return { error: 'Nie udało się wyodrębnić business_id z URL Booksy.' };
}

function BooksyCard() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const b = (brief as BriefShape | undefined)?.booking;
  const current = b?.booksy ?? null;
  const [open, setOpen] = React.useState(false);

  const saveBinding = async (next: BooksyBinding | null) => {
    const prevBooking = (brief as BriefShape | undefined)?.booking ?? {};
    const nextBooking = next
      ? { ...prevBooking, provider: 'booksy', booksy: next }
      : { ...prevBooking, provider: prevBooking.provider === 'booksy' ? 'none' : prevBooking.provider, booksy: undefined };
    await update.mutateAsync({ slice: 'booking', data: nextBooking });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="size-5" aria-hidden="true" /> Rezerwacja online — Booksy
        </CardTitle>
        <CardDescription>
          Jeśli klinika ma profil w Booksy, wklej jego adres — kalendarz pojawi się na stronie
          jako wbudowany widget. Alternatywa dla znanylekarz.pl (konfigurowanego przy zespole).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {current ? (
          <div className="space-y-2 rounded-md bg-success/5 p-4 ring-1 ring-success/20">
            <p className="text-sm font-medium text-text">
              Booksy aktywne
              <span className="ml-2 text-xs text-text-subtle">
                business_id: <span className="font-mono">{current.business_id}</span>
              </span>
            </p>
            <p className="break-all text-xs text-text-muted">
              <a
                href={current.profile_url}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                {current.profile_url}
              </a>
            </p>
            <p className="text-xs text-text-subtle">
              Wariant: <span className="font-mono">{current.widget_type ?? 'full'}</span>
              {current.hide_branding ? ' · bez logo Booksy' : ' · z logo'}
            </p>
            <div className="flex gap-2 pt-2">
              <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
                Edytuj
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" size="sm" variant="ghost">
                    Usuń powiązanie
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Usunąć powiązanie z Booksy?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Widżet Booksy zniknie ze strony przy następnej publikacji. Konfigurację
                      będzie można odtworzyć ponownie wklejając URL profilu.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Anuluj</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        await saveBinding(null);
                        toast.success('Powiązanie z Booksy usunięte');
                      }}
                    >
                      Usuń
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : (
          <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
            Połącz z Booksy
          </Button>
        )}

        {open ? (
          <BooksyWizard
            initial={current}
            onClose={() => setOpen(false)}
            onSave={async (next) => {
              await saveBinding(next);
              setOpen(false);
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function BooksyWizard({
  initial,
  onClose,
  onSave,
}: {
  initial: BooksyBinding | null;
  onClose: () => void;
  onSave: (next: BooksyBinding) => void;
}) {
  const [url, setUrl] = React.useState(initial?.profile_url ?? '');
  const [widgetType, setWidgetType] = React.useState<'full' | 'compact'>(
    initial?.widget_type ?? 'full'
  );
  const [hideBranding, setHideBranding] = React.useState(initial?.hide_branding ?? false);
  const parsed = url ? parseBooksyUrl(url) : null;
  const ok = parsed && 'business_id' in parsed;
  const previewSrc = ok
    ? `https://booksy.com/widget/index.html?id=${parsed.business_id}${
        hideBranding ? '&hideBranding=true' : ''
      }`
    : null;

  return (
    <div className="mt-4 space-y-3 rounded-md bg-bg p-4 ring-1 ring-accent/30">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
        Krok 1 — wklej URL profilu
      </p>
      <Field
        label="URL profilu booksy.com"
        htmlFor="booksy-url"
        hint="Z paska adresu na booksy.com — np. https://booksy.com/pl-pl/123456,klinika-abc"
        error={parsed && 'error' in parsed ? parsed.error : undefined}
      >
        <Input
          id="booksy-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://booksy.com/pl-pl/123456,klinika-abc"
        />
      </Field>

      {ok ? (
        <>
          <div className="rounded-md bg-surface p-3 text-xs text-text-muted ring-1 ring-black/5">
            Wykryto business_id: <span className="font-mono text-text">{parsed.business_id}</span>
          </div>

          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Krok 2 — wariant widżetu
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(['full', 'compact'] as const).map((wt) => (
              <button
                type="button"
                key={wt}
                onClick={() => setWidgetType(wt)}
                className={cn(
                  'rounded-md p-2 text-xs ring-1 transition',
                  widgetType === wt
                    ? 'bg-accent/10 text-accent ring-accent/30'
                    : 'bg-bg text-text-muted ring-border hover:ring-black/10'
                )}
              >
                {wt === 'full' ? 'Pełny (z kalendarzem)' : 'Kompaktowy'}
              </button>
            ))}
          </div>
          <label className="flex items-center justify-between gap-3 rounded-md bg-surface p-3 text-sm text-text ring-1 ring-black/5">
            <span>Ukryj branding Booksy na widżecie</span>
            <Switch checked={hideBranding} onCheckedChange={setHideBranding} />
          </label>

          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Krok 3 — podgląd
          </p>
          {previewSrc ? (
            <iframe
              title="Podgląd widżetu Booksy"
              src={previewSrc}
              className="h-72 w-full rounded-md ring-1 ring-black/5"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : null}
        </>
      ) : null}

      <div className="flex justify-end gap-2 border-t border-border pt-3">
        <Button type="button" variant="ghost" onClick={onClose}>
          <X className="size-4" aria-hidden="true" /> Anuluj
        </Button>
        <Button
          type="button"
          disabled={!ok}
          onClick={() =>
            ok &&
            onSave({
              business_id: parsed.business_id,
              profile_url: parsed.normalized_url,
              widget_type: widgetType,
              hide_branding: hideBranding,
            })
          }
        >
          {initial ? 'Zapisz' : 'Połącz'}
        </Button>
      </div>
    </div>
  );
}

function DomainsCard() {
  const domains = useDomains();
  const add = useAddDomain();
  const refresh = useRefreshDomain();
  const remove = useDeleteDomain();
  const [host, setHost] = React.useState('');

  const onAdd = async () => {
    try {
      await add.mutateAsync(host.trim());
      setHost('');
      toast.success('Domena dodana — skonfiguruj CNAME u rejestratora');
    } catch (e) {
      const m = (e as Error).message;
      toast.error(
        m === 'invalid_hostname'
          ? 'Nieprawidłowy format domeny.'
          : m === 'host_taken'
          ? 'Ta domena jest już używana.'
          : m === 'cannot_add_managed_domain'
          ? 'Nie można dodać domeny zarządzanej przez nas.'
          : m === 'cf_provisioning_failed'
          ? 'Cloudflare odmówił rejestracji. Sprawdź konfigurację konta.'
          : 'Nie udało się dodać domeny.'
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Własna domena</CardTitle>
        <CardDescription>
          Dodaj swoją domenę (np. mojadomena.pl). Po wprowadzeniu rekordu CNAME u twojego
          dostawcy DNS automatycznie wygenerujemy certyfikat TLS.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {domains.data && !domains.data.cf_configured ? (
          <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
            Cloudflare for SaaS nie jest skonfigurowany. Domenę zarejestrujemy ręcznie po
            zgłoszeniu do operatora.
          </p>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (host.trim()) onAdd();
          }}
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
        >
          <div className="flex-1">
            <Field label="Domena" htmlFor="new-domain" hint="np. dentivet.pl">
              <Input
                id="new-domain"
                placeholder="mojadomena.pl"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </Field>
          </div>
          <Button type="submit" disabled={!host.trim() || add.isPending}>
            {add.isPending ? 'Dodawanie…' : 'Dodaj domenę'}
          </Button>
        </form>

        {domains.data?.domains?.length ? (
          <ul role="list" className="space-y-3">
            {domains.data.domains.map((d) => (
              <DomainRowCard
                key={d.host}
                domain={d}
                cnameTarget={domains.data!.cname_target}
                onRefresh={() => {
                  refresh.mutate(d.host, {
                    onSuccess: () => toast.success(`Status ${d.host} odświeżony`),
                    onError: () => toast.error('Nie udało się odświeżyć statusu'),
                  });
                }}
                onDelete={() =>
                  remove.mutate(d.host, {
                    onSuccess: () => toast.success(`Domena ${d.host} usunięta`),
                    onError: () => toast.error('Nie udało się usunąć domeny'),
                  })
                }
                busy={refresh.isPending || remove.isPending}
              />
            ))}
          </ul>
        ) : (
          <p className="rounded-md bg-surface p-3 text-sm text-text-muted ring-1 ring-black/5">
            Brak domen. Dodaj pierwszą, aby uruchomić publiczny adres swojej strony.
          </p>
        )}

        <RegistrarDocs cnameTarget={domains.data?.cname_target ?? 'custom.pozyskajpacjenta.pl'} />
      </CardContent>
    </Card>
  );
}

/**
 * Inline how-to for the top three PL domain registrars. Each tab shows
 * the exact CNAME + TXT records to paste, in the registrar's vocabulary
 * (most use slightly different field names + propagation times).
 */
function RegistrarDocs({ cnameTarget }: { cnameTarget: string }) {
  return (
    <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <p className="text-sm font-semibold text-text">Jak ustawić DNS u Twojego rejestratora?</p>
      <p className="mt-1 text-xs text-text-muted">
        Wybierz rejestratora poniżej. Wszystkie kroki kończą się tym samym: CNAME
        wskazujący na <span className="font-mono">{cnameTarget}</span>. Czas propagacji:
        5–60 minut w większości przypadków.
      </p>
      <Tabs defaultValue="home" className="mt-3">
        <TabsList>
          <TabsTrigger value="home">home.pl</TabsTrigger>
          <TabsTrigger value="ovh">OVH</TabsTrigger>
          <TabsTrigger value="nazwa">nazwa.pl</TabsTrigger>
          <TabsTrigger value="other">Inny</TabsTrigger>
        </TabsList>
        <TabsContent value="home">
          <ol className="ml-4 list-decimal space-y-1 text-xs text-text">
            <li>Zaloguj się w <span className="font-mono">home.pl</span> → „Moja domena".</li>
            <li>Wybierz domenę → zakładka „DNS / Strefa DNS / Edytuj rekordy".</li>
            <li>Usuń istniejący rekord A dla głównej domeny (jeśli był).</li>
            <li>
              Dodaj rekord <strong>CNAME</strong>: pole „Host" puste lub{' '}
              <span className="font-mono">@</span>, „Wartość":{' '}
              <span className="font-mono">{cnameTarget}</span>, TTL: 600.
            </li>
            <li>
              Jeśli dodajesz wariant www: drugi rekord CNAME, host{' '}
              <span className="font-mono">www</span>, wartość ta sama.
            </li>
            <li>Zapisz. Wróć tutaj i kliknij przycisk „Odśwież" przy domenie powyżej.</li>
          </ol>
        </TabsContent>
        <TabsContent value="ovh">
          <ol className="ml-4 list-decimal space-y-1 text-xs text-text">
            <li>OVH Manager → Domeny → wybierz domenę.</li>
            <li>Zakładka „Strefa DNS" → „Dodaj wpis".</li>
            <li>
              Typ wpisu: <strong>CNAME</strong>. „Subdomena": puste (dla głównej) lub{' '}
              <span className="font-mono">www</span>.
            </li>
            <li>
              „Cel": <span className="font-mono">{cnameTarget}.</span> (z kropką na końcu).
            </li>
            <li>TTL: 600 (domyślne 3600 też ok).</li>
            <li>Zapisz. Propagacja zwykle 5–15 min na OVH.</li>
          </ol>
        </TabsContent>
        <TabsContent value="nazwa">
          <ol className="ml-4 list-decimal space-y-1 text-xs text-text">
            <li>Panel <span className="font-mono">nazwa.pl</span> → „Domeny" → wybierz.</li>
            <li>„Konfiguracja DNS" → „DNS edycja".</li>
            <li>
              Wyłącz „Automatyczna konfiguracja DNS" jeśli włączona — przejdź na ręczną.
            </li>
            <li>
              Dodaj <strong>CNAME</strong>, host: <span className="font-mono">@</span> lub puste,
              wartość: <span className="font-mono">{cnameTarget}</span>.
            </li>
            <li>Powtórz dla <span className="font-mono">www</span> jeśli używasz.</li>
            <li>
              Uwaga: nazwa.pl czasem wymaga dodatkowo usunięcia istniejących rekordów MX ich
              systemu pocztowego — zrób to <strong>tylko</strong> jeśli nie używasz tego maila.
            </li>
          </ol>
        </TabsContent>
        <TabsContent value="other">
          <p className="text-xs text-text-subtle">
            Inny rejestrator? Skontaktuj się z nami pisząc na adres pomocy — przygotujemy
            instrukcję dla Twojego dostawcy.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DomainRowCard({
  domain,
  cnameTarget,
  onRefresh,
  onDelete,
  busy,
}: {
  domain: DomainRow;
  cnameTarget: string;
  onRefresh: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [copied, setCopied] = React.useState<string | null>(null);
  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const statusBadge = (() => {
    const styles: Record<DomainRow['status'], string> = {
      pending: 'bg-warning/10 text-warning ring-warning/20',
      verified: 'bg-accent/10 text-accent ring-accent/20',
      live: 'bg-success/10 text-success ring-success/20',
      failed: 'bg-error/10 text-error ring-error/20',
      suspended: 'bg-surface text-text-muted ring-border',
    };
    const labels: Record<DomainRow['status'], string> = {
      pending: 'Oczekuje na DNS',
      verified: 'Weryfikacja TLS',
      live: 'Live',
      failed: 'Błąd',
      suspended: 'Wstrzymana',
    };
    return (
      <span className={cn('rounded-full px-2.5 py-0.5 text-xs ring-1', styles[domain.status])}>
        {labels[domain.status]}
      </span>
    );
  })();

  return (
    <li className="rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="font-display text-base font-semibold text-text">{domain.host}</p>
            {statusBadge}
          </div>
          {domain.last_check_error ? (
            <p className="mt-1 text-xs text-error">{domain.last_check_error}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Odśwież status"
            disabled={busy}
            onClick={onRefresh}
          >
            <RefreshCw className="size-4" aria-hidden="true" />
          </Button>
          {domain.status === 'live' ? (
            <a
              href={`https://${domain.host}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex size-9 items-center justify-center rounded-md text-text-muted hover:text-text"
              aria-label="Otwórz w nowej karcie"
            >
              <ExternalLink className="size-4" aria-hidden="true" />
            </a>
          ) : null}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Usuń domenę"
                disabled={busy}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Usunąć domenę {domain.host}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Strona przestanie być dostępna pod tym adresem. Certyfikat TLS zostanie
                  zwolniony. Domenę można dodać ponownie w dowolnym momencie.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Anuluj</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete}>Usuń</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {domain.status !== 'live' ? (
        <div className="mt-4 space-y-3 rounded-md bg-surface p-3 ring-1 ring-black/5">
          <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">
            Krok 1 · Skonfiguruj DNS
          </p>
          <p className="text-sm text-text">
            Dodaj u dostawcy DNS rekord CNAME, który wskaże domenę na nasz adres:
          </p>
          <div className="grid grid-cols-[5rem_1fr_2.5rem] items-center gap-2 font-mono text-xs">
            <span className="text-text-subtle">CNAME</span>
            <span className="rounded bg-bg px-2 py-1 ring-1 ring-border">{domain.host}</span>
            <span aria-hidden="true">→</span>
          </div>
          <div className="grid grid-cols-[5rem_1fr_2.5rem] items-center gap-2 font-mono text-xs">
            <span className="text-text-subtle">cel</span>
            <span className="rounded bg-bg px-2 py-1 ring-1 ring-border">{cnameTarget}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Skopiuj cel"
              onClick={() => copy(cnameTarget)}
            >
              {copied === cnameTarget ? (
                <Check className="size-4 text-success" aria-hidden="true" />
              ) : (
                <Copy className="size-4" aria-hidden="true" />
              )}
            </Button>
          </div>
          <p className="text-xs text-text-muted">
            Po zmianie DNS kliknij ↻ powyżej. Propagacja zwykle trwa do 15 minut.
          </p>
        </div>
      ) : (
        <p className="mt-3 text-sm text-text-muted">
          Strona dostępna pod{' '}
          <a
            href={`https://${domain.host}`}
            className="text-accent underline-offset-4 hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            https://{domain.host}
          </a>
          .
        </p>
      )}
    </li>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-surface p-3 ring-1 ring-black/5">
      <span className="text-text-muted">{label}</span>
      <span className={mono ? 'font-mono text-xs text-text' : 'text-text'}>{value}</span>
    </div>
  );
}
